import { useState } from 'react';
import { Inquiry } from '@/mocks/inquiries';
import ReplyModal from './ReplyModal';

interface InquiriesListProps {
  inquiries: Inquiry[];
  onUpdateInquiry: (id: string, updates: Partial<Inquiry>) => void;
  notificationEmail?: string;
}

const statusColors: Record<string, string> = {
  new: 'bg-red-50 text-red-600',
  read: 'bg-yellow-50 text-yellow-600',
  replied: 'bg-[#E8F5EE] text-[#1B6B3A]',
};

const statusLabels: Record<string, string> = {
  new: 'New',
  read: 'Read',
  replied: 'Replied',
};

const FORM_TYPES = [
  { key: 'all', label: 'All Forms', icon: 'ri-inbox-line' },
  { key: 'Contact Us', label: 'Contact Us', icon: 'ri-mail-line' },
  { key: 'Pickup Request', label: 'Pickup Request', icon: 'ri-truck-line' },
  { key: 'Drop-off Request', label: 'Drop-off', icon: 'ri-map-pin-line' },
  { key: 'Event Request', label: 'Event Request', icon: 'ri-calendar-event-line' },
];

const serviceToFormType = (service: string): string => {
  if (service === 'Pickup Request') return 'Pickup Request';
  if (service === 'Drop-off Request') return 'Drop-off Request';
  if (service === 'Event Request') return 'Event Request';
  return 'Contact Us';
};

const parseMessageFields = (message: string): { label: string; value: string }[] => {
  if (!message.includes(':')) return [{ label: 'Message', value: message }];
  const lines = message.split('\n').filter((l) => l.trim());
  const fields: { label: string; value: string }[] = [];
  let currentLabel = '';
  let currentValue = '';
  lines.forEach((line) => {
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0 && colonIdx < 40) {
      if (currentLabel) fields.push({ label: currentLabel, value: currentValue.trim() });
      currentLabel = line.slice(0, colonIdx).trim();
      currentValue = line.slice(colonIdx + 1).trim();
    } else {
      currentValue += ' ' + line.trim();
    }
  });
  if (currentLabel) fields.push({ label: currentLabel, value: currentValue.trim() });
  return fields.filter((f) => f.value);
};

const InquiriesList = ({ inquiries, onUpdateInquiry, notificationEmail }: InquiriesListProps) => {
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filtered = inquiries.filter((inq) => {
    const matchStatus = filterStatus === 'all' || inq.status === filterStatus;
    const matchType = filterType === 'all' || serviceToFormType(inq.service) === filterType;
    const matchSearch =
      search === '' ||
      inq.name.toLowerCase().includes(search.toLowerCase()) ||
      inq.email.toLowerCase().includes(search.toLowerCase()) ||
      inq.message.toLowerCase().includes(search.toLowerCase()) ||
      inq.service.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchType && matchSearch;
  });

  const handleOpen = (inq: Inquiry) => {
    setExpandedId(expandedId === inq.id ? null : inq.id);
    if (inq.status === 'new') {
      onUpdateInquiry(inq.id, { status: 'read' });
    }
  };

  const handleReply = (id: string, reply: string) => {
    onUpdateInquiry(id, {
      reply,
      status: 'replied',
      replyDate: new Date().toISOString(),
    });
    setSelectedInquiry(null);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Company', 'Service', 'Status', 'Message', 'Reply', 'Reply Date'];
    const rows = filtered.map((inq) => [
      new Date(inq.date).toLocaleString(),
      inq.name,
      inq.email,
      inq.phone || '',
      inq.company || '',
      inq.service,
      inq.status,
      inq.message.replace(/"/g, '""'),
      inq.reply ? inq.reply.replace(/"/g, '""') : '',
      inq.replyDate ? new Date(inq.replyDate).toLocaleString() : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${cell}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `greenly-inquiries-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildGmailUrl = (inq: Inquiry, body?: string) => {
    const to = encodeURIComponent(inq.email);
    const subject = encodeURIComponent(`Re: Your inquiry with Greenly Electronics Recycling`);
    const defaultBody = `Hi ${inq.name},\n\nThank you for reaching out to Greenly Electronics Recycling.\n\n`;
    const encodedBody = encodeURIComponent(body || defaultBody);
    const authUser = notificationEmail ? `&authuser=${encodeURIComponent(notificationEmail)}` : '';
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${encodedBody}${authUser}`;
  };

  return (
    <div>
      {/* No email configured banner */}
      {!notificationEmail && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-error-warning-line text-amber-500"></i>
          </div>
          <p className="text-sm text-amber-700 flex-1">
            No Gmail notification address configured.{' '}
            <button
              onClick={() => {/* handled by parent tab switch */}}
              className="font-semibold underline cursor-pointer"
            >
              Go to Settings
            </button>{' '}
            to set up your Gmail address for inquiry alerts.
          </p>
        </div>
      )}

      {/* Form Type Filter */}
      <div className="flex flex-wrap gap-2 mb-3">
        {FORM_TYPES.map((ft) => {
          const count = ft.key === 'all'
            ? inquiries.length
            : inquiries.filter((i) => serviceToFormType(i.service) === ft.key).length;
          return (
            <button
              key={ft.key}
              onClick={() => setFilterType(ft.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                filterType === ft.key
                  ? 'bg-[#0F3D22] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#1B6B3A] hover:text-[#1B6B3A]'
              }`}
            >
              <i className={ft.icon}></i>
              {ft.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${filterType === ft.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Status + Search Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
            <i className="ri-search-line text-gray-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search inquiries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'read', 'replied'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors capitalize ${
                filterStatus === s ? 'bg-[#1B6B3A] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#1B6B3A] hover:text-[#1B6B3A]'
              }`}
            >
              {s === 'all' ? 'All' : statusLabels[s]}
              <span className="ml-1.5 text-xs opacity-70">
                ({inquiries.filter((i) => s === 'all' || i.status === s).length})
              </span>
            </button>
          ))}
          <button
            onClick={exportToCSV}
            className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 text-sm font-medium px-4 py-2.5 rounded-xl hover:border-[#1B6B3A] hover:text-[#1B6B3A] cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-download-line"></i>
            Export CSV
          </button>
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-14 text-gray-400">
          <i className="ri-inbox-line text-4xl block mb-3"></i>
          <p>No inquiries found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((inq) => (
            <div key={inq.id} className={`bg-white rounded-xl overflow-hidden border ${inq.status === 'new' ? 'border-[#1B6B3A]/30' : 'border-gray-100'}`}>
              {/* Header row */}
              <button
                onClick={() => handleOpen(inq)}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer text-left"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0">
                  <i className="ri-user-line text-[#1B6B3A]"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{inq.name}</span>
                    {inq.company && <span className="text-gray-400 text-xs">{inq.company}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[inq.status]}`}>
                      {statusLabels[inq.status]}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500 inline-flex items-center gap-1">
                      <i className={`${FORM_TYPES.find((f) => f.key === serviceToFormType(inq.service))?.icon ?? 'ri-file-line'} text-xs`}></i>
                      {serviceToFormType(inq.service)}
                    </span>
                  </div>
                  <div className="text-gray-400 text-xs mt-0.5 truncate">
                    {inq.email} {inq.phone && `· ${inq.phone}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-gray-400 text-xs">{formatDate(inq.date)}</div>
                </div>
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i className={`text-gray-400 transition-transform ${expandedId === inq.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </div>
              </button>

              {/* Expanded */}
              {expandedId === inq.id && (
                <div className="px-5 pb-5 border-t border-gray-100">

                  {/* Contact details grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 mb-4">
                    {[
                      { icon: 'ri-mail-line', label: 'Email', value: inq.email, href: `mailto:${inq.email}` },
                      { icon: 'ri-phone-line', label: 'Phone', value: inq.phone || '—', href: inq.phone ? `tel:${inq.phone}` : undefined },
                      { icon: 'ri-building-line', label: 'Company', value: inq.company || '—', href: undefined },
                      { icon: 'ri-customer-service-2-line', label: 'Service', value: inq.service, href: undefined },
                    ].map((detail) => (
                      <div key={detail.label} className="bg-[#F4FAF7] rounded-xl p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <i className={`${detail.icon} text-[#1B6B3A] text-sm`}></i>
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">{detail.label}</span>
                        </div>
                        {detail.href ? (
                          <a href={detail.href} className="text-sm font-medium text-[#1B6B3A] hover:underline cursor-pointer truncate block">{detail.value}</a>
                        ) : (
                          <p className="text-sm font-medium text-gray-800 truncate">{detail.value}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Message / form fields */}
                  <div className="bg-[#F4FAF7] rounded-xl p-4 mb-4">
                    <div className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-wide flex items-center gap-1.5">
                      <i className="ri-file-text-line text-[#1B6B3A]"></i> Submission Details
                    </div>
                    {(() => {
                      const fields = parseMessageFields(inq.message);
                      if (fields.length === 1 && fields[0].label === 'Message') {
                        return <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">{inq.message}</p>;
                      }
                      return (
                        <div className="space-y-2.5">
                          {fields.map((f) => (
                            <div key={f.label} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                              <span className="text-xs font-bold text-gray-400 uppercase tracking-wide sm:w-36 shrink-0 pt-0.5">{f.label}</span>
                              <span className="text-base text-gray-800 leading-relaxed">{f.value}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Reply */}
                  {inq.reply && (
                    <div className="bg-[#E8F5EE] rounded-xl p-4 mb-4">
                      <div className="text-xs font-bold text-[#1B6B3A] mb-2 uppercase tracking-wide flex items-center gap-1.5">
                        <i className="ri-reply-line"></i> Your Reply
                        {inq.replyDate && <span className="font-normal text-[#1B6B3A]/60 normal-case tracking-normal">· {formatDate(inq.replyDate)}</span>}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed">{inq.reply}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedInquiry(inq)}
                      className="inline-flex items-center gap-1.5 bg-[#1B6B3A] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#2D8653] cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-reply-line"></i>
                      {inq.reply ? 'Update Reply' : 'Reply'}
                    </button>
                    <a
                      href={buildGmailUrl(inq)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#EA4335] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#c5372b] cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-google-line"></i> Reply via Gmail
                      {notificationEmail && <span className="text-white/70 text-xs font-normal hidden lg:inline">({notificationEmail})</span>}
                    </a>
                    <a
                      href={`mailto:${inq.email}`}
                      className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-600 text-sm font-medium px-4 py-2 rounded-full hover:border-[#1B6B3A] hover:text-[#1B6B3A] cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-mail-line"></i> Email Directly
                    </a>
                    {inq.phone && (
                      <a
                        href={`tel:${inq.phone}`}
                        className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-600 text-sm font-medium px-4 py-2 rounded-full hover:border-[#1B6B3A] hover:text-[#1B6B3A] cursor-pointer whitespace-nowrap transition-colors"
                      >
                        <i className="ri-phone-line"></i> Call
                      </a>
                    )}
                    <button
                      onClick={() => {
                        const updated = { ...inq, status: inq.status === 'new' ? 'read' : inq.status === 'read' ? 'replied' : 'new' } as Inquiry;
                        onUpdateInquiry(inq.id, { status: updated.status });
                      }}
                      className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-500 text-sm font-medium px-4 py-2 rounded-full hover:border-gray-400 cursor-pointer whitespace-nowrap transition-colors ml-auto"
                    >
                      <i className="ri-refresh-line"></i> Change Status
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedInquiry && (
        <ReplyModal
          inquiry={selectedInquiry}
          onClose={() => setSelectedInquiry(null)}
          onReply={handleReply}
          notificationEmail={notificationEmail}
        />
      )}
    </div>
  );
};

export default InquiriesList;
